import { Search, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { trpc } from "../lib/trpc";

/** Sotto le due lettere non si interroga il server: la ricerca partirebbe su mezzo archivio. */
const MIN_LENGTH = 2;

export function GlobalSearch() {
  const inputId = useId();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const trimmed = query.trim();
  const enabled = trimmed.length >= MIN_LENGTH;
  const results = trpc.search.global.useQuery({ query: trimmed }, { enabled });
  const groups = results.data ?? [];
  const total = groups.reduce((sum, gruppo) => sum + gruppo.hits.length, 0);

  useEffect(() => {
    if (!open) return;

    const closeOnOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutside);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  function apri(href: string) {
    setOpen(false);
    setQuery("");
    navigate(href);
  }

  return (
    <div className="global-search" ref={containerRef} role="search">
      <label className="global-search__field" htmlFor={inputId}>
        <Search size={18} aria-hidden="true" />
        <span className="sr-only">Cerca in tutta l'app</span>
        <input
          id={inputId}
          type="search"
          value={query}
          placeholder="Cerca in tutta l'app"
          autoComplete="off"
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        {query && (
          <button
            type="button"
            className="global-search__clear"
            aria-label="Cancella ricerca"
            onClick={() => {
              setQuery("");
              setOpen(false);
            }}
          >
            <X size={16} />
          </button>
        )}
      </label>

      {open && enabled && (
        <div className="global-search__panel" role="region" aria-label="Risultati della ricerca">
          {results.isLoading && <p className="global-search__note">Sto cercando...</p>}
          {results.isError && <p className="global-search__note">Ricerca non disponibile in questo momento.</p>}
          {!results.isLoading && !results.isError && total === 0 && (
            <p className="global-search__note">Nessun risultato per «{trimmed}».</p>
          )}
          {groups.map((gruppo) => (
            <section key={gruppo.key} className="global-search__group">
              <h2>{gruppo.label}</h2>
              <ul>
                {gruppo.hits.map((hit) => (
                  <li key={`${gruppo.key}:${hit.id}`}>
                    <button type="button" onClick={() => apri(hit.href)}>
                      <strong>{hit.title}</strong>
                      {hit.detail && <small>{hit.detail}</small>}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
